// This file is the main entry point for the theme. It is used to import all the global files for the theme to work.
import 'vite/modulepreload-polyfill'

// Vue components, local to our custom theme.
import { createApp } from "vue";
import MiniCart from "@/js/vue/mini-cart.vue";

// Render the sidebar mini cart
if (document.querySelector("#mini-cart")) {
  createApp(MiniCart).mount("#mini-cart");
}
//Render on the sidebar mini cart on the cart page
if (document.querySelector("#mini-cart-page")) {
  createApp(MiniCart).mount("#mini-cart-page");
}

// Helpers, local to our custom theme.
import "@/js/helpers/video-plays-in-view";
import "@/js/helpers/collection-filter";
import "@/js/helpers/variant-form-handler";
import "@/js/helpers/recently-viewed-tracker";
import "@/js/helpers/fly-out-panel-toggle";
import toggleBodyClass from "@/js/helpers/toggle-body-class";
import loadComponentIfNeeded from "@/js/helpers/load-component-if-needed";

// Import directly from Horizon theme /asstes/ folder using @theme alias.
// Import RecentlyViewed to expose globally
import { RecentlyViewed } from "@theme/recently-viewed-products";
import "@theme/dialog";
import "@theme/predictive-search";
import "@theme/facets";
import "@theme/localization";
import "@theme/quick-add";

// Components, local to our custom theme
import PDPAddToCartButton from "../js/components/pdp-add-to-cart-button";
import QuantitySelector from "@/js/components/quantity-selector";
import MegaMenu from "@/js/components/mega-menu";
import FooterAccordion from "@/js/components/footer-accordion";
import UniversalAccordion from "@/js/components/universal-accordion";
import TabbedPanels from "@/js/components/tabbed-panels";
import VariantPicker from "@theme/variant-picker";
import plpCard from "@/js/components/plp-card";
import "@/js/components/price-range-slider";
import "@/js/components/ajax-filter-handler";
import CustomSlideshow from "@/js/components/custom-slideshow";
import LoyaltyPopup from "@/js/components/loyalty-popup";

// Define critical Web Components immediately
if (!customElements.get("loyalty-popup")) {
  customElements.define("loyalty-popup", LoyaltyPopup);
}
if (!customElements.get("plp-card")) {
  customElements.define("plp-card", plpCard); 
}
if (!customElements.get("quantity-input")) {
  customElements.define("quantity-input", QuantitySelector);
}
if (!customElements.get("mega-menu")) {
  customElements.define("mega-menu", MegaMenu);
}
if (!customElements.get("footer-accordion")) {
  customElements.define("footer-accordion", FooterAccordion);
}
if (!customElements.get("universal-accordion")) {
  customElements.define("universal-accordion", UniversalAccordion);
}
if (!customElements.get("tabbed-panels")) {
  customElements.define("tabbed-panels", TabbedPanels);
}
if (!customElements.get("variant-picker")) {
  customElements.define("variant-picker", VariantPicker);
}
if (!customElements.get("pdp-add-to-cart-button")) {
  customElements.define("pdp-add-to-cart-button", PDPAddToCartButton);
}
if (!customElements.get('slideshow-component')) {
  customElements.define('slideshow-component', CustomSlideshow);
}
// Load components only when their elements exist on the page
loadComponentIfNeeded("klaviyo-form", "klaviyo-form", () => import("@/js/components/klaviyo-form"));
// swiper-component must be eagerly loaded as it's used in sticky ATC which may be hidden initially
import("@/js/components/swiper").then(module => {
  if (!customElements.get("swiper-component")) {
    customElements.define("swiper-component", module.default);
  }
});
loadComponentIfNeeded("ajax-pagination", "ajax-pagination", () => import("@/js/components/ajax-pagination"));
loadComponentIfNeeded("audio-player", "audio-player", () => import("@/js/components/audio-player"));
loadComponentIfNeeded("ticker-tape", "ticker-tape", () => import("@/js/components/ticker-tape"));
loadComponentIfNeeded("fbt-block", "fbt-block", () => import("@/js/components/fbt-block"));


// Expose helper functions globally
declare global {
  interface Window {
    toggleBodyClass: typeof toggleBodyClass;
    RecentlyViewed: typeof RecentlyViewed;
  }
}

window.toggleBodyClass = toggleBodyClass;
window.RecentlyViewed = RecentlyViewed;

// Click handler to trigger native loyalty widget (Essential Loyalty or Smile.io) when custom Rewards button is clicked
document.addEventListener("click", (e) => {
  const target = e.target as HTMLElement;
  if (target.closest("[data-open-loyalty-popup]")) {
    e.preventDefault();
    
    // 1. Try to open Essential Loyalty / Honeypop
    const nativeLauncher = document.querySelector("#honeypop-app button") as HTMLElement | null
      || document.querySelector(".essential-loyalty-app-button") as HTMLElement | null
      || document.querySelector("#honeypop-app a[type='button']") as HTMLElement | null
      || document.querySelector("#honeypop-app [class*='_Button_']") as HTMLElement | null;
    if (nativeLauncher) {
      nativeLauncher.click();
      return;
    }
    
    // 2. Try to open Smile.io (via iframe contentDocument click)
    const smileIframe = document.getElementById('smile-lite-launcher-frame') as HTMLIFrameElement | null
      || document.querySelector('iframe[src*="smile"]') as HTMLIFrameElement | null;
    if (smileIframe) {
      try {
        const doc = smileIframe.contentDocument || smileIframe.contentWindow?.document;
        if (doc) {
          const btn = doc.querySelector('button') as HTMLElement | null
            || doc.querySelector('[role="button"]') as HTMLElement | null
            || doc.querySelector('a') as HTMLElement | null;
          if (btn) {
            btn.click();
            return;
          }
        }
      } catch (err) {
        console.error("Error clicking Smile launcher button:", err);
      }
    }
    
    // 3. Try fallback Smile.io JS APIs
    if (typeof window !== "undefined") {
      const Smile = (window as any).Smile;
      const SmileUI = (window as any).SmileUI;
      if (Smile && typeof Smile.showPanel === "function") {
        Smile.showPanel();
        return;
      }
      if (SmileUI && typeof SmileUI.open === "function") {
        SmileUI.open();
        return;
      }
    }
    
    console.warn("No active loyalty launcher (Essential Loyalty or Smile.io) found in DOM.");
  }
});

// Auto-open native loyalty widget after 5 seconds on the homepage template
document.addEventListener("DOMContentLoaded", () => {
  const isHomepage = document.body.classList.contains("page-index") || 
                     window.location.pathname === "/" || 
                     window.location.pathname === "/challenge";
                     
  if (isHomepage) {
    let attempts = 0;
    const interval = setInterval(() => {
      // Try Essential Loyalty / Honeypop
      const nativeLauncher = document.querySelector("#honeypop-app button") as HTMLElement | null
        || document.querySelector(".essential-loyalty-app-button") as HTMLElement | null
        || document.querySelector("#honeypop-app a[type='button']") as HTMLElement | null
        || document.querySelector("#honeypop-app [class*='_Button_']") as HTMLElement | null;

      if (nativeLauncher) {
        clearInterval(interval);
        setTimeout(() => {
          nativeLauncher.click();
        }, 5000); // 5s delay after landing on the page
        return;
      }

      // Try Smile.io (via iframe contentDocument click)
      const smileIframe = document.getElementById('smile-lite-launcher-frame') as HTMLIFrameElement | null
        || document.querySelector('iframe[src*="smile"]') as HTMLIFrameElement | null;
      if (smileIframe) {
        try {
          const doc = smileIframe.contentDocument || smileIframe.contentWindow?.document;
          if (doc) {
            const btn = doc.querySelector('button') as HTMLElement | null
              || doc.querySelector('[role="button"]') as HTMLElement | null
              || doc.querySelector('a') as HTMLElement | null;
            if (btn) {
              clearInterval(interval);
              setTimeout(() => {
                btn.click();
              }, 5000); // 5s delay after landing on the page
              return;
            }
          }
        } catch (err) {
          // Ignore CORS exceptions during lookup and let attempts continue
        }
      }

      attempts++;
      if (attempts > 50) { // Stop polling after 10 seconds
        clearInterval(interval);
        
        // Fallback for Smile.io JS APIs
        if (typeof window !== "undefined") {
          const Smile = (window as any).Smile;
          const SmileUI = (window as any).SmileUI;
          if (Smile && typeof Smile.showPanel === "function") {
            Smile.showPanel();
          } else if (SmileUI && typeof SmileUI.open === "function") {
            SmileUI.open();
          }
        }
      }
    }, 200);
  }
});


// horizon web components
// TODO Could register them all through on entry point file like this?
// import { inithorizonComponents } from "@theme/js/components/init";
// window.customElements.define("dialog-component", DialogComponent);
// window.customElements.define('predictive-search-component', PredictiveSearchComponent);
// window.customElements.define('declarative-shadow-element', DeclarativeShadowElement);

